import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FieldArray } from 'redux-form';
import {
  InputFieldValueComponent,
  CheckBoxFieldValueComponent,
  SelectFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import AppFields from './apps/AppFields';
import { ONBOARDING_TEMPLATE_FORM_NAME } from '../../constants';
import { normalizeTag } from '../../../app/helpers/normalizeTag';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import communityAppsService from '../../services/communityApps/communityAppsService';
import communityRolesService from '../../services/communityRoles/communityRolesService';

const REQUIRED_FIELDS = ['name'];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const OnboardingTemplateForm = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  editMode,
  communityAppsFetch,
  dispatchCommunityAppsGet,
  dispatchCommunityRolesGet,
  communityRolesFetch,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  useEffect(() => dispatchCommunityAppsGet(id, t), [dispatchCommunityAppsGet, id, t]);
  useEffect(() => dispatchCommunityRolesGet(id, t), [dispatchCommunityRolesGet, id, t]);

  const communityRoleOptions = useMemo(
    () =>
      (communityRolesFetch.value?.communityRoles || []).map(r => ({
        key: r.tag,
        value: r.tag,
        text: r.alias || r.name,
      })),
    [communityRolesFetch.value]
  );

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={ONBOARDING_TEMPLATE_FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      <Grid>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('name')}
            name="name"
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('tag')}
            name="tag"
            normalize={normalizeTag}
            valueComponent={InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            multiple
            search
            clearable
            options={communityRoleOptions}
            label={t('communityRoles_plural')}
            name="communityRoles"
            valueComponent={SelectFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            toggle
            label={t('isGloballyManaged')}
            name="isInternal"
            valueComponent={CheckBoxFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <FieldArray
            name="apps"
            component={AppFields}
            apps={communityAppsFetch.value}
            submitProcessing={submitProcessing}
            editMode={editMode}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

OnboardingTemplateForm.propTypes = {
  dispatchCommunityAppsGet: PropTypes.func.isRequired,
  communityAppsFetch: PropTypes.object.isRequired,
  dispatchCommunityRolesGet: PropTypes.func.isRequired,
  communityRolesFetch: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
  editMode: PropTypes.bool,
};

OnboardingTemplateForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  data: {},
  editMode: false,
};

export default compose(communityRolesService, communityAppsService)(OnboardingTemplateForm);
